/**
 * User Management - Admin Settings Page (DC-627)
 *
 * Admin interface for managing organization users:
 * - View all users with their roles and status
 * - Invite new users to the organization
 * - Change user roles (admin, analyst, viewer)
 * - Deactivate/activate users
 * - Revoke user sessions
 *
 * @version 1.0.0
 * @date 2026-01-02
 * @epic DC-6 Multi-Tenancy & IAM
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import { useAuth, useAuthenticatedFetch } from '../../hooks/useAuth';
import './user-management.css';

// =============================================================================
// TYPES
// =============================================================================

type UserRole = 'admin' | 'analyst' | 'viewer';
type UserStatus = 'active' | 'invited' | 'inactive';

interface User {
  user_id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  email_verified: boolean;
  last_login_at?: string;
  created_at: string;
}

interface InviteUserData {
  email: string;
  name: string;
  role: UserRole;
}

/**
 * Accept-link surfaced to the admin after invite/resend/copy-link (v2.5.1).
 * emailSent is null when unknown (link fetched from the pending list rather
 * than returned by a create/resend response).
 */
interface InviteLinkInfo {
  email: string;
  url: string;
  emailSent: boolean | null;
}

/**
 * Copy text to the clipboard, with a fallback for non-secure origins where
 * navigator.clipboard is unavailable (on-prem HTTP deployments). Returns
 * whether the copy succeeded — the caller keeps the link visible either way.
 */
async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the legacy path
    }
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  } catch {
    return false;
  }
}

const INITIAL_INVITE_DATA: InviteUserData = {
  email: '',
  name: '',
  role: 'analyst',
};

const ROLE_OPTIONS: { value: UserRole; label: string; description: string }[] = [
  { value: 'viewer', label: 'Viewer', description: 'Can view journeys and results' },
  { value: 'analyst', label: 'Analyst', description: 'Can create and run analyses' },
  { value: 'admin', label: 'Admin', description: 'Full access including user management' },
];

const STATUS_LABELS: Record<UserStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'status-active' },
  invited: { label: 'Invited', className: 'status-invited' },
  inactive: { label: 'Inactive', className: 'status-inactive' },
};

// =============================================================================
// USER ROW COMPONENT
// =============================================================================

interface UserRowProps {
  user: User;
  currentUserId: string;
  onChangeRole: (user: User, newRole: UserRole) => void;
  onToggleStatus: (user: User) => void;
  onRevokeSessions: (user: User) => void;
  onOffboard: (user: User) => void;
  onResendInvite: (user: User) => void;
  onRevokeInvite: (user: User) => void;
  onCopyInviteLink: (user: User) => void;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  currentUserId,
  onChangeRole,
  onToggleStatus,
  onRevokeSessions,
  onOffboard,
  onResendInvite,
  onRevokeInvite,
  onCopyInviteLink,
}) => {
  const [showActions, setShowActions] = useState(false);
  const isCurrentUser = user.user_id === currentUserId;
  // 'invited' rows carry an invitation token id, not a user id — the
  // user/role/status/session endpoints would 404. Show invitation-specific
  // controls instead of the user-mutation controls (UM-2).
  const isInvited = user.status === 'invited';
  const roleLabel = ROLE_OPTIONS.find((o) => o.value === user.role)?.label || user.role;

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const statusInfo = STATUS_LABELS[user.status];

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChangeRole(user, e.target.value as UserRole);
  };

  return (
    <tr className={`user-row ${user.status === 'inactive' ? 'inactive' : ''}`}>
      <td className="user-cell">
        <div className="user-info">
          <div className="user-avatar">{initials}</div>
          <div className="user-details">
            <span className="user-name">
              {user.name}
              {isCurrentUser && <span className="current-user-badge">(You)</span>}
            </span>
            {!user.email_verified && user.status === 'active' && (
              <span className="email-unverified">Email not verified</span>
            )}
          </div>
        </div>
      </td>
      <td className="email-cell">{user.email}</td>
      <td className="role-cell">
        {isInvited ? (
          <span className={`role-static role-${user.role}`} title="Role is set when the invitation is accepted">
            {roleLabel}
          </span>
        ) : (
          <select
            className={`role-select role-${user.role}`}
            value={user.role}
            onChange={handleRoleChange}
            disabled={isCurrentUser}
            title={isCurrentUser ? 'Cannot change your own role' : 'Change role'}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
      </td>
      <td className="status-cell">
        <span className={`status-badge ${statusInfo.className}`}>
          <span className="status-dot" />
          {statusInfo.label}
        </span>
      </td>
      <td className="actions-cell">
        <div className="actions-menu-container">
          <button
            className="actions-trigger"
            onClick={() => setShowActions(!showActions)}
            aria-label="Actions"
          >
            ⋮
          </button>
          {showActions && (
            <>
              <div className="actions-backdrop" onClick={() => setShowActions(false)} />
              <div className="actions-menu">
                {isInvited ? (
                  <>
                    <button
                      className="action-item"
                      onClick={() => {
                        setShowActions(false);
                        onCopyInviteLink(user);
                      }}
                    >
                      Copy Invite Link
                    </button>
                    <button
                      className="action-item"
                      onClick={() => {
                        setShowActions(false);
                        onResendInvite(user);
                      }}
                    >
                      Resend Invitation
                    </button>
                    <button
                      className="action-item action-item-danger"
                      onClick={() => {
                        setShowActions(false);
                        onRevokeInvite(user);
                      }}
                    >
                      Revoke Invitation
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="action-item"
                      onClick={() => {
                        setShowActions(false);
                        onToggleStatus(user);
                      }}
                      disabled={isCurrentUser}
                    >
                      {user.status === 'inactive' ? 'Activate User' : 'Deactivate User'}
                    </button>
                    <button
                      className="action-item"
                      onClick={() => {
                        setShowActions(false);
                        onRevokeSessions(user);
                      }}
                      disabled={isCurrentUser}
                    >
                      Revoke All Sessions
                    </button>
                    <button
                      className="action-item action-item-danger"
                      onClick={() => {
                        setShowActions(false);
                        onOffboard(user);
                      }}
                      disabled={isCurrentUser}
                    >
                      Offboard User
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// =============================================================================
// INVITE USER MODAL
// =============================================================================

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteUserData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onInvite,
  isLoading,
  error,
}) => {
  const [formData, setFormData] = useState<InviteUserData>(INITIAL_INVITE_DATA);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onInvite(formData);
  };

  const handleChange = (field: keyof InviteUserData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    setFormData(INITIAL_INVITE_DATA);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invite a Team Member</h2>
          <button className="modal-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="invite-email">Email Address</label>
            <input
              id="invite-email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="colleague@company.com"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="invite-name">Full Name</label>
            <input
              id="invite-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="New Colleague"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <div className="role-options">
              {ROLE_OPTIONS.map((opt) => (
                <label key={opt.value} className="role-option">
                  <input
                    type="radio"
                    name="role"
                    value={opt.value}
                    checked={formData.role === opt.value}
                    onChange={() => handleChange('role', opt.value)}
                    disabled={isLoading}
                  />
                  <div className="role-option-content">
                    <span className="role-option-label">{opt.label}</span>
                    <span className="role-option-description">{opt.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const UserManagement: React.FC = () => {
  const { user: currentUser, hasRole } = useAuth();
  const { authFetch } = useAuthenticatedFetch();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<UserRole | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<UserStatus | 'all'>('all');

  // Pagination (UM-6). The server caps the page at 50 users + pending invites;
  // the footer now reports the true server total, not the page size.
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<{ page: number; total: number; totalPages: number }>({
    page: 1,
    total: 0,
    totalPages: 1,
  });

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Action feedback
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Invite-link banner (v2.5.1) — keeps the accept URL visible for
  // out-of-band delivery on deployments without email
  const [inviteLink, setInviteLink] = useState<InviteLinkInfo | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Check if current user is admin
  const isAdmin = hasRole('admin');

  // =============================================================================
  // FETCH USERS
  // =============================================================================

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch(`/api/iam/users?page=${page}`);
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      setUsers(data.users || []);
      if (data.pagination) {
        setPagination({
          page: data.pagination.page ?? page,
          total: data.pagination.total ?? (data.users?.length || 0),
          totalPages: data.pagination.totalPages ?? 1,
        });
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [authFetch, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // =============================================================================
  // USER ACTIONS
  // =============================================================================

  const handleInviteUser = async (data: InviteUserData) => {
    try {
      setInviteLoading(true);
      setInviteError(null);

      const response = await authFetch('/api/iam/users/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Server error shape is { error: { code, message }, meta } — read the
        // nested message so the real reason (e.g. "already exists") surfaces
        // instead of the generic fallback (UM-3).
        throw new Error(body.error?.message || body.message || 'Failed to send invitation');
      }

      setShowInviteModal(false);
      const emailSent: boolean = body.data?.emailSent === true;
      if (body.data?.acceptUrl) {
        setInviteLink({ email: data.email, url: body.data.acceptUrl, emailSent });
        setLinkCopied(false);
      }
      setActionMessage({
        type: 'success',
        text: emailSent
          ? `Invitation emailed to ${data.email}`
          : `Invitation created for ${data.email} — no email was sent, share the invite link below`,
      });
      fetchUsers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleChangeRole = async (user: User, newRole: UserRole) => {
    try {
      const response = await authFetch(`/api/iam/users/${user.user_id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw new Error('Failed to change role');
      }

      setActionMessage({ type: 'success', text: `${user.name}'s role changed to ${newRole}` });
      fetchUsers();
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to change user role' });
    }
  };

  const handleToggleStatus = async (user: User) => {
    const newStatus = user.status === 'inactive' ? 'active' : 'inactive';
    try {
      const response = await authFetch(`/api/iam/users/${user.user_id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      const action = newStatus === 'active' ? 'activated' : 'deactivated';
      setActionMessage({ type: 'success', text: `${user.name} has been ${action}` });
      fetchUsers();
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to update user status' });
    }
  };

  const handleRevokeSessions = async (user: User) => {
    if (!confirm(`Are you sure you want to revoke all sessions for ${user.name}? They will be logged out immediately.`)) {
      return;
    }

    try {
      const response = await authFetch(`/api/iam/users/${user.user_id}/sessions`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to revoke sessions');
      }

      setActionMessage({ type: 'success', text: `All sessions revoked for ${user.name}` });
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to revoke sessions' });
    }
  };

  const handleOffboard = async (user: User) => {
    if (!confirm(`Offboard ${user.name}? This deactivates the account, revokes all sessions, and revokes their API keys. This cannot be undone from here.`)) {
      return;
    }
    try {
      const response = await authFetch(`/api/iam/users/${user.user_id}/offboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error('Failed to offboard user');
      }
      setActionMessage({ type: 'success', text: `${user.name} has been offboarded` });
      fetchUsers();
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to offboard user' });
    }
  };

  const handleResendInvite = async (user: User) => {
    try {
      const response = await authFetch(`/api/iam/users/invitations/${user.user_id}/resend`, {
        method: 'POST',
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error?.message || 'Failed to resend invitation');
      }
      const emailSent: boolean = body.data?.emailSent === true;
      if (body.data?.acceptUrl) {
        setInviteLink({ email: user.email, url: body.data.acceptUrl, emailSent });
        setLinkCopied(false);
      }
      setActionMessage({
        type: 'success',
        text: emailSent
          ? `Invitation resent to ${user.email}`
          : `Invitation refreshed for ${user.email} — no email was sent, share the invite link below`,
      });
      fetchUsers();
    } catch (err) {
      setActionMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to resend invitation' });
    }
  };

  const handleCopyInviteLink = async (user: User) => {
    try {
      const response = await authFetch(`/api/iam/users/invitations/${user.user_id}/link`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error?.message || 'Failed to retrieve invite link');
      }
      const url: string = body.data?.acceptUrl;
      if (!url) {
        throw new Error('Failed to retrieve invite link');
      }
      setInviteLink({ email: user.email, url, emailSent: null });
      const copied = await copyText(url);
      setLinkCopied(copied);
      setActionMessage(
        copied
          ? { type: 'success', text: `Invite link for ${user.email} copied to clipboard` }
          : { type: 'success', text: `Invite link for ${user.email} shown below — copy it manually` },
      );
    } catch (err) {
      setActionMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to retrieve invite link' });
    }
  };

  const handleCopyBannerLink = async () => {
    if (!inviteLink) return;
    const copied = await copyText(inviteLink.url);
    setLinkCopied(copied);
    if (copied) {
      setActionMessage({ type: 'success', text: `Invite link for ${inviteLink.email} copied to clipboard` });
    }
  };

  const handleRevokeInvite = async (user: User) => {
    if (!confirm(`Revoke the pending invitation for ${user.email}?`)) {
      return;
    }
    try {
      const response = await authFetch(`/api/iam/users/invitations/${user.user_id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error?.message || 'Failed to revoke invitation');
      }
      setActionMessage({ type: 'success', text: `Invitation revoked for ${user.email}` });
      fetchUsers();
    } catch (err) {
      setActionMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to revoke invitation' });
    }
  };

  // Clear action message after 5 seconds
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => setActionMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  // =============================================================================
  // FILTER USERS
  // =============================================================================

  const filteredUsers = users.filter((user) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!user.name.toLowerCase().includes(query) && !user.email.toLowerCase().includes(query)) {
        return false;
      }
    }

    // Role filter
    if (filterRole !== 'all' && user.role !== filterRole) {
      return false;
    }

    // Status filter
    if (filterStatus !== 'all' && user.status !== filterStatus) {
      return false;
    }

    return true;
  });

  // =============================================================================
  // RENDER
  // =============================================================================

  if (!isAdmin) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You need administrator privileges to access user management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      {/* Header */}
      <div className="management-header">
        <div className="header-left">
          <h2>Team Members</h2>
          <p className="header-description">
            Manage user accounts and permissions for your organization
          </p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
            + Invite User
          </button>
        </div>
      </div>

      {/* Action feedback */}
      {actionMessage && (
        <div className={`action-message ${actionMessage.type}`}>
          {actionMessage.text}
        </div>
      )}

      {/* Invite-link banner — the accept URL stays visible so it can be
          delivered over Teams/chat when email delivery is not configured */}
      {inviteLink && (
        <div className="invite-link-banner" role="status">
          <div className="invite-link-info">
            <span className="invite-link-label">
              Invite link for <strong>{inviteLink.email}</strong>
              {inviteLink.emailSent === false &&
                ' — no invitation email was sent (email delivery is not configured on this instance); send this link to them directly'}
            </span>
            <input
              className="invite-link-url"
              readOnly
              value={inviteLink.url}
              onFocus={(e) => e.target.select()}
              aria-label={`Invite link for ${inviteLink.email}`}
            />
          </div>
          <div className="invite-link-actions">
            <button className="btn btn-secondary" onClick={handleCopyBannerLink}>
              {linkCopied ? 'Copied' : 'Copy Link'}
            </button>
            <button
              className="btn btn-secondary invite-link-dismiss"
              onClick={() => setInviteLink(null)}
              aria-label="Dismiss invite link"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-input-wrapper">
          <Search className="search-icon" size={14} aria-hidden="true" />
          <input
            type="text"
            className="search-input"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Role:</label>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value as UserRole | 'all')}
          >
            <option value="all">All Roles</option>
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as UserStatus | 'all')}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users table */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading users...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchUsers}>
            Retry
          </button>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">
          <p>No users found</p>
          {searchQuery || filterRole !== 'all' || filterStatus !== 'all' ? (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSearchQuery('');
                setFilterRole('all');
                setFilterStatus('all');
              }}
            >
              Clear Filters
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
              Invite your first team member
            </button>
          )}
        </div>
      ) : (
        <>
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <UserRow
                  key={user.user_id}
                  user={user}
                  currentUserId={currentUser?.id || ''}
                  onChangeRole={handleChangeRole}
                  onToggleStatus={handleToggleStatus}
                  onRevokeSessions={handleRevokeSessions}
                  onOffboard={handleOffboard}
                  onResendInvite={handleResendInvite}
                  onRevokeInvite={handleRevokeInvite}
                  onCopyInviteLink={handleCopyInviteLink}
                />
              ))}
            </tbody>
          </table>
          <div className="table-footer">
            <span>
              Showing {filteredUsers.length} of {pagination.total} users
            </span>
            {pagination.totalPages > 1 && (
              <span className="pager">
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </button>
                <span className="pager-status">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                </button>
              </span>
            )}
          </div>
        </>
      )}

      {/* Invite Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          setInviteError(null);
        }}
        onInvite={handleInviteUser}
        isLoading={inviteLoading}
        error={inviteError}
      />
    </div>
  );
};

export default UserManagement;
